// Shared review workspace for Sentinel Responses and Client Feedback.

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSentinelConversations, useSentinelConversation, useSubmitSentinelReview } from "@/hooks/use-sentinel";
import { usePermissions } from "@/hooks/use-permissions";
import type { SentinelListFilters } from "@/services/sentinelReview.service";
import { Download } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TicketList } from "@/components/shared/ticket-list";
import { SentinelThread, type SentinelManageActions } from "@/modules/sentinel/sentinel-thread";
import { SentinelFeedbackPanel, type SentinelReviewDraft } from "@/modules/sentinel/sentinel-feedback-panel";
import { SentinelFeedbackView } from "@/modules/sentinel/sentinel-feedback-view";
import { SentinelExportDialog } from "@/modules/sentinel/sentinel-export-dialog";

/** Shape of the infinite-query result `list.refetch()` resolves with. */
interface RefetchedConversationPages {
  pages: Array<{ conversations: Array<{ conversationId: number }> }>;
}

const SEARCH_DEBOUNCE_MS = 350;

// "all" is the UI-only value for no review-status filter
const REVIEW_STATUS_ALL = "all";

export function SentinelReviewWorkspace({
  listTitle = "Sentinel Responses",
  readOnly = false,
  allowReadOnlyActions = true,
}: {
  listTitle?: string;
  readOnly?: boolean;
  allowReadOnlyActions?: boolean;
}) {
  const [searchInput, setSearchInput] = useState("");
  // Review mode starts with unreviewed conversations; read-only mode shows all.
  const [filters, setFilters] = useState<SentinelListFilters>(
    readOnly ? {} : { reviewStatus: "not_reviewed" }
  );
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  const list = useSentinelConversations(filters);
  const detail = useSentinelConversation(selectedConversationId ?? undefined);
  const { submit, remove, setAddressed, editResponse, removeResponse, removeConversation, isSubmitting } =
    useSubmitSentinelReview();
  // Deleting / rewriting Kim's responses is an administrator-only grant; the
  // backend enforces it too, this only decides whether the controls render.
  const canManageResponses = usePermissions().can("edit", "client-review.manage");

  // Debounce the contact search into the query filters.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => {
        const nextSearch = searchInput.trim();
        if ((prev.contactSearch ?? "") === nextSearch) return prev;
        const next: SentinelListFilters = { ...prev };
        if (nextSearch) next.contactSearch = nextSearch;
        else delete next.contactSearch;
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Select the first conversation when the list loads.
  useEffect(() => {
    if (selectedConversationId === null && list.data.length > 0) {
      setSelectedConversationId(list.data[0]!.conversationId);
    }
  }, [list.data, selectedConversationId]);

  // Keep the empty turns array stable between renders.
  const turns = useMemo(() => detail.data?.turns ?? [], [detail.data]);

  // Only turns with a Sentinel response can be reviewed.
  const reviewableTurns = useMemo(() => turns.filter((turn) => turn.sentinelMessage != null), [turns]);

  // Keep a valid response selected, preferring the latest reviewed response.
  useEffect(() => {
    if (reviewableTurns.length === 0) {
      if (selectedMessageId !== null) setSelectedMessageId(null);
      return;
    }
    if (!reviewableTurns.some((turn) => turn.sentinelMessage?.id === selectedMessageId)) {
      const preferred = readOnly ? [...reviewableTurns].reverse().find((turn) => turn.review != null) : null;
      const fallback = reviewableTurns[reviewableTurns.length - 1]!;
      const target = preferred ?? fallback;
      if (target.sentinelMessage) setSelectedMessageId(target.sentinelMessage.id);
    }
  }, [reviewableTurns, selectedMessageId, readOnly]);

  const selectedTurn = reviewableTurns.find((turn) => turn.sentinelMessage?.id === selectedMessageId) ?? null;

  // Apply the reviewed / not-reviewed filter, or clear it when "All" is picked.
  const handleReviewStatusChange = useCallback((reviewStatus: SentinelListFilters["reviewStatus"]) => {
    setFilters((prev) => {
      const next: SentinelListFilters = { ...prev };
      if (reviewStatus) next.reviewStatus = reviewStatus;
      else delete next.reviewStatus;
      return next;
    });
  }, []);

  const handleSelectConversation = useCallback((conversationId: number) => {
    setSelectedConversationId(conversationId);
    setSelectedMessageId(null);
  }, []);

  const refresh = useCallback(() => {
    list.refetch();
    detail.refetch();
  }, [list, detail]);

  const handleSubmit = useCallback(
    async (draft: SentinelReviewDraft) => {
      if (!selectedTurn?.sentinelMessage || selectedConversationId === null) return;
      const ok = await submit({
        conversationId: selectedConversationId,
        sentinelMessageId: selectedTurn.sentinelMessage.id,
        verdict: draft.verdict,
        reasons: draft.reasons,
        ...(draft.otherText ? { otherText: draft.otherText } : {}),
        ...(draft.rewrite ? { rewrite: draft.rewrite } : {}),
      });
      if (!ok) {
        toast.error("Could not save your feedback. Please try again.");
        return;
      }
      toast.success("Feedback saved");
      refresh();
    },
    [selectedTurn, selectedConversationId, submit, refresh],
  );

  const handleDelete = useCallback(async () => {
    if (!selectedTurn?.review) return;
    const ok = await remove(selectedTurn.review.id);
    if (!ok) {
      toast.error("Could not delete your feedback.");
      return;
    }
    toast.success("Feedback deleted");
    refresh();
  }, [selectedTurn, remove, refresh]);

  const handleToggleAddressed = useCallback(
    async (addressed: boolean) => {
      if (!selectedTurn?.review) return;
      const ok = await setAddressed(selectedTurn.review.id, addressed);
      if (!ok) {
        toast.error("Could not update the feedback.");
        return;
      }
      toast.success(addressed ? "Marked as addressed" : "Marked as open");
      refresh();
    },
    [selectedTurn, setAddressed, refresh],
  );

  const handleDeleteConversation = useCallback(async () => {
    if (selectedConversationId === null) return false;
    const ok = await removeConversation(selectedConversationId);
    if (!ok) {
      toast.error("Could not delete the conversation.");
      return false;
    }
    toast.success("Conversation removed from the review dashboard");
    // Refetch before clearing the selection: the auto-select effect reads the
    // list, and the stale one still holds the conversation just deleted.
    await list.refetch();
    setSelectedConversationId(null);
    setSelectedMessageId(null);
    return true;
  }, [selectedConversationId, removeConversation, list]);

  const handleDeleteResponse = useCallback(
    async (sentinelMessageId: number) => {
      const ok = await removeResponse(sentinelMessageId);
      if (!ok) {
        toast.error("Could not delete the response.");
        return false;
      }
      toast.success("Response deleted");
      const [refreshed] = await Promise.all([list.refetch(), detail.refetch()]);
      // The list keys off the newest remaining response, so the conversation may
      // now be gone (no Kim responses left, or it fell outside the active
      // review-status filter). Don't keep showing a thread the list no longer has.
      const stillListed = (refreshed.data as RefetchedConversationPages | undefined)?.pages.some((page) =>
        page.conversations.some((conversation) => conversation.conversationId === selectedConversationId),
      );
      if (!stillListed) {
        setSelectedConversationId(null);
        setSelectedMessageId(null);
      }
      return true;
    },
    [removeResponse, list, detail, selectedConversationId],
  );

  const handleEditResponse = useCallback(
    async (sentinelMessageId: number, content: string) => {
      const ok = await editResponse(sentinelMessageId, content);
      if (!ok) {
        toast.error("Could not save the response.");
        return false;
      }
      toast.success("Response updated");
      refresh();
      return true;
    },
    [editResponse, refresh],
  );

  const manage = useMemo<SentinelManageActions | undefined>(
    () =>
      canManageResponses && !readOnly
        ? {
            isBusy: isSubmitting,
            onDeleteConversation: handleDeleteConversation,
            onDeleteResponse: handleDeleteResponse,
            onEditResponse: handleEditResponse,
          }
        : undefined,
    [canManageResponses, readOnly, isSubmitting, handleDeleteConversation, handleDeleteResponse, handleEditResponse],
  );

  // Remount the form per selected response / review revision so it re-initialises.
  const feedbackKey = useMemo(
    () => `${selectedConversationId ?? "none"}-${selectedMessageId ?? "none"}-${selectedTurn?.review?.id ?? "new"}`,
    [selectedConversationId, selectedMessageId, selectedTurn],
  );

  // The list and review panels never shrink, so below their combined 700px the
  // thread column was squeezed to ZERO and `overflow-hidden` clipped the review
  // panel off-screen with no scrollbar — the agent simply could not reach it.
  // A minimum on the thread keeps the actual content readable, and the
  // workspace scrolls rather than hiding a panel. Above ~940px nothing changes.
  return (
    <div className="flex h-screen w-full overflow-x-auto">
      <div className="w-[320px] min-w-0 shrink-0 overflow-hidden">
        <TicketList
          variant="review"
          title={listTitle}
          headerActions={
            <>
              {/* Filter the list by whether the Sentinel response has been reviewed. */}
              <Select
                value={filters.reviewStatus ?? REVIEW_STATUS_ALL}
                onValueChange={(value) =>
                  handleReviewStatusChange(
                    value === REVIEW_STATUS_ALL ? undefined : (value as "reviewed" | "not_reviewed"),
                  )
                }
              >
                <SelectTrigger className="shrink-0" size="sm">
                  <SelectValue placeholder="Review status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={REVIEW_STATUS_ALL}>All</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="not_reviewed">Not reviewed</SelectItem>
                </SelectContent>
              </Select>
              {readOnly && allowReadOnlyActions && (
                <button
                  type="button"
                  onClick={() => setExportOpen(true)}
                  title="Export reviews as CSV"
                  aria-label="Export reviews"
                  className="flex size-8 cursor-pointer items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted"
                >
                  <Download className="size-4" />
                </button>
              )}
            </>
          }
          items={list.data.map((conversation) => ({
            id: conversation.conversationId,
            subject:
              conversation.subject?.trim() ||
              conversation.contact?.name ||
              `Conversation #${conversation.conversationId}`,
            customerName: conversation.contact?.name ?? null,
            preview: conversation.preview,
            category: conversation.categories?.[0] ?? null,
            timestamp: conversation.lastSentinelAt,
            pendingCount: conversation.pendingCount,
          }))}
          groupByCategory
          selectedId={selectedConversationId}
          isLoading={list.isLoading}
          search={{ value: searchInput, onChange: setSearchInput }}
          paging={{
            hasMore: list.hasMore,
            isFetchingNextPage: list.isFetchingNextPage,
            onLoadMore: list.fetchNextPage,
          }}
          onSelect={handleSelectConversation}
        />
      </div>

      <div className="min-w-[240px] flex-1">
        <SentinelThread
          header={detail.data?.conversation ?? null}
          turns={turns}
          selectedMessageId={selectedMessageId}
          isLoading={detail.isLoading}
          readOnly={readOnly}
          manage={manage}
          onSelectResponse={setSelectedMessageId}
        />
      </div>

      <div className="w-[380px] shrink-0">
        {!selectedTurn ? (
          <div className="flex h-full items-center justify-center border-l border-border bg-background px-6 text-center text-sm text-muted-foreground">
            {detail.isLoading ? "Loading…" : "Select a response to review."}
          </div>
        ) : readOnly ? (
          <SentinelFeedbackView
            review={selectedTurn.review}
            isBusy={isSubmitting}
            onToggleAddressed={allowReadOnlyActions ? handleToggleAddressed : undefined}
          />
        ) : (
          <SentinelFeedbackPanel
            key={feedbackKey}
            review={selectedTurn.review}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
          />
        )}
      </div>

      <SentinelExportDialog open={exportOpen} onOpenChange={setExportOpen} />
    </div>
  );
}
